import { ApplicationRef, EventEmitter, Injectable } from '@angular/core'
import { finalize, pluck, tap } from 'rxjs/operators'
import { BehaviorSubject, Observable } from 'rxjs'
import {
    AcceptDealOfferGQL,
    AddSigningPersonToDealGQL,
    MakeDealOfferGQL,
    UpdateBooleanDealOptionMutation,
    UpdateBooleanDealOptionMutationVariables,
    UpdateBooleanDealOptionGQL,
    UpdateDealBillingGQL,
    UpdateDealContactGQL,
    UpdateDealDriverGQL,
    UpdateDealLesseeGQL,
    UpdateDealManufacturerAccountEmailAddressGQL,
    UpdateDealManufacturerReferralCodeGQL,
    UpdateDealReferralCodeGQL,
    UpdateSelectableDealOptionMutation,
    UpdateSelectableDealOptionMutationVariables,
    UpdateSelectableDealOptionGQL,
} from '../../../generated/graphql'
import { ErrorService } from '../../../ui/monolith/services/error.service'
import { FetchResult } from '@apollo/client/core'

@Injectable({
    providedIn: 'root',
})
export class DealMutationService {
    isUpdating: BehaviorSubject<boolean> = new BehaviorSubject(false)
    optionUpdated: EventEmitter<{
        name: string
        deal:
            | UpdateSelectableDealOptionMutation['updateSelectableDealOption']
            | UpdateBooleanDealOptionMutation['updateBooleanDealOption']
    }> = new EventEmitter()
    private updateTrackers: Map<string, BehaviorSubject<number>> = new Map()

    constructor(
        private acceptDealOfferGQL: AcceptDealOfferGQL,
        private addSigningPersonToDealGQL: AddSigningPersonToDealGQL,
        private makeDealOfferGQL: MakeDealOfferGQL,
        private updateSelectableDealOptionGQL: UpdateSelectableDealOptionGQL,
        private updateBooleanDealOptionGQL: UpdateBooleanDealOptionGQL,
        private updateDealBillingGQL: UpdateDealBillingGQL,
        private updateDealContactGQL: UpdateDealContactGQL,
        private updateDealDriverGQL: UpdateDealDriverGQL,
        private updateDealLesseeGQL: UpdateDealLesseeGQL,
        private updateDealManufacturerAccountEmailAddressGQL: UpdateDealManufacturerAccountEmailAddressGQL,
        private updateDealManufacturerReferralCodeGQL: UpdateDealManufacturerReferralCodeGQL,
        private updateDealReferralCodeGQL: UpdateDealReferralCodeGQL,
        private errorService: ErrorService,
        private applicationRef: ApplicationRef
    ) {}

    /* 
    HELPER FUNCTIONS
  */

    acceptDealOffer(id) {
        return this.acceptDealOfferGQL
            .mutate({ id })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    /*
    API
  */

    // Mutations to change the deal status

    makeDealOffer(dealId) {
        return this.makeDealOfferGQL
            .mutate({ dealId })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, dealId),
                })
            )
            .toPromise()
    }

    updateDealBilling(id, input) {
        input = {
            ...input,
            iban: input.iban.replace(/\s/g, ''),
        }

        return this.updateDealBillingGQL
            .mutate({ id, input })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    // Simple mutations (driver, billing, lessee, etc.)

    updateDealReferralCode(id, code) {
        return this.updateDealReferralCodeGQL
            .mutate({ id, code })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    updateDealContact(id, input) {
        return this.updateDealContactGQL
            .mutate({ id, input })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    updateDealDriver(id, email) {
        return this.updateDealDriverGQL
            .mutate({ id, email })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    updateDealLessee(id, companyValue) {
        const input = { ...companyValue, houseNumber: parseInt(companyValue.houseNumber) }

        return this.updateDealLesseeGQL
            .mutate({ id, input })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    updateDealManufacturerAccountEmailAddress(id, email) {
        return this.updateDealManufacturerAccountEmailAddressGQL
            .mutate({ id, email })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    updateDealManufacturerReferralCode(id, code) {
        return this.updateDealManufacturerReferralCodeGQL
            .mutate({ id, code })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    addSigningPerson(id, email) {
        return this.addSigningPersonToDealGQL
            .mutate({ id, email })
            .pipe(
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                })
            )
            .toPromise()
    }

    updateDealOption(id, option, value): Observable<UpdateSelectableDealOptionMutation['updateSelectableDealOption']> {
        if (option.__typename !== 'SelectableDealOption' && option.__typename !== 'BooleanDealOption') {
            console.warn(
                'Attempting to update an option which is neither a SelectableDealOption nor a BooleanDealOption'
            )
            return
        }

        this.addUpdate(option.name)

        if (option.__typename === 'SelectableDealOption') {
            const mutationVariables: UpdateSelectableDealOptionMutationVariables = {
                id,
                dealOptionId: option.id,
                valueId: value,
            }

            return this.updateSelectableDealOptionGQL.mutate(mutationVariables).pipe(
                pluck<
                    FetchResult<UpdateSelectableDealOptionMutation>,
                    UpdateSelectableDealOptionMutation['updateSelectableDealOption']
                >('data', 'updateSelectableDealOption'),
                tap((deal: UpdateSelectableDealOptionMutation['updateSelectableDealOption']) => {
                    this.optionUpdated.emit({
                        name: option.name,
                        deal,
                    })
                }),
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                }),
                finalize(() => this.subtractUpdate(option.name))
            )
        } else if (option.__typename === 'BooleanDealOption') {
            const mutationVariables: UpdateBooleanDealOptionMutationVariables = {
                id,
                dealOptionId: option.id,
                value,
            }

            return this.updateBooleanDealOptionGQL.mutate(mutationVariables).pipe(
                pluck<
                    FetchResult<UpdateBooleanDealOptionMutation>,
                    UpdateBooleanDealOptionMutation['updateBooleanDealOption']
                >('data', 'updateBooleanDealOption'),
                tap((deal: UpdateBooleanDealOptionMutation['updateBooleanDealOption']) => {
                    this.optionUpdated.emit({
                        name: option.name,
                        deal,
                    })
                }),
                this.errorService.observableCatcher({
                    catchFn: err => this.updateDealErrorCatchFn(err, id),
                }),
                finalize(() => this.subtractUpdate(option.name))
            )
        }
    }

    // More complicated mutations (car options)

    getUpdateTracker(name: string) {
        if (!this.updateTrackers.has(name)) {
            this.updateTrackers.set(name, new BehaviorSubject(0))
        }

        return this.updateTrackers.get(name)
    }

    private updateDealErrorCatchFn(err, id) {
        // if you have multiple windows open you could accidentally try to
        // overwrite a configuration that already has been approved
        // forcefully reload to allow correct routing of approved config
        if (err.message.indexOf('configuration has already been approved') > -1) {
            window.location.href = `/checkout/${id}`
        }
    }

    /**
     * Mark a name as updating
     *
     * @param name
     */
    private addUpdate(name: string) {
        const updateTracker = this.getUpdateTracker(name)
        updateTracker.next(updateTracker.getValue() + 1)
        this.syncIsUpdating()
    }

    /**
     * subtract an update action from a name
     * @param name
     */
    private subtractUpdate(name: string) {
        setTimeout(
            () => {
                const updateTracker = this.getUpdateTracker(name)
                updateTracker.next((updateTracker.getValue() || 1) - 1)
                this.syncIsUpdating()

                // force update within application
                this.applicationRef.tick()
            },
            name === 'edition' ? 100 : 1
        )
    }

    /**
     * sync isUpdating state, will be true if a update is running
     */
    private syncIsUpdating() {
        const isUpdating = Array.from(this.updateTrackers.values()).some(updateTracker => updateTracker.getValue() > 0)

        if (isUpdating !== this.isUpdating.getValue()) {
            this.isUpdating.next(isUpdating)
        }
    }
}
